<template>
  <div class="unified-query-native">
    <!-- 智能仪表板 -->
    <div class="dashboard-section">
      <div class="dashboard-header">
        <h2 class="section-title">
          <i class="icon-analytics"></i>
          AI财务数据指挥中心
        </h2>
        <button class="btn btn-primary" @click="showAllData" :class="{ 'active': isShowingAll() }">
          <i class="icon-apps"></i>
          显示全部
        </button>
      </div>
      <div class="dashboard-cards">
        <div class="stat-card" @click="filterByType('documents')" :class="{ 'active': isCardActive('documents') }">
          <div class="card-icon document-icon">🎫</div>
          <div class="card-content">
            <h3>票据管理</h3>
            <div class="stat-number">{{ statistics.documentStats.totalCount || 0 }}</div>
            <div class="stat-details">
              <span>已归并: {{ statistics.documentStats.mergedCount || 0 }}</span>
              <span>已关联: {{ statistics.documentStats.relatedCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选票据</div>
        </div>

        <div class="stat-card" @click="filterByType('receipts')" :class="{ 'active': isCardActive('receipts') }">
          <div class="card-icon receipt-icon">💳</div>
          <div class="card-content">
            <h3>银行回单</h3>
            <div class="stat-number">{{ statistics.receiptStats.totalCount || 0 }}</div>
            <div class="stat-details">
              <span>已归并: {{ statistics.receiptStats.mergedCount || 0 }}</span>
              <span>已关联: {{ statistics.receiptStats.relatedCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选银行回单</div>
        </div>

        <div class="stat-card" @click="filterByType('merge-groups')" :class="{ 'active': isCardActive('merge-groups') }">
          <div class="card-icon group-icon">📦</div>
          <div class="card-content">
            <h3>归并组</h3>
            <div class="stat-number">{{ (statistics.mergeGroupStats.documentGroupCount || 0) + (statistics.mergeGroupStats.receiptGroupCount || 0) }}</div>
            <div class="stat-details">
              <span>票据组: {{ statistics.mergeGroupStats.documentGroupCount || 0 }}</span>
              <span>银证组: {{ statistics.mergeGroupStats.receiptGroupCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选归并组</div>
        </div>

        <div class="stat-card" @click="filterByType('relations')" :class="{ 'active': isCardActive('relations') }">
          <div class="card-icon relation-icon">🔗</div>
          <div class="card-content">
            <h3>关联关系</h3>
            <div class="stat-number">{{ statistics.relationStats.totalRelationCount || 0 }}</div>
            <div class="stat-details">
              <span>AI生成: {{ statistics.relationStats.aiGeneratedRelationCount || 0 }}</span>
              <span>手动创建: {{ statistics.relationStats.manualRelationCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选已关联</div>
        </div>

        <div class="stat-card" @click="filterByType('vouchers')" :class="{ 'active': isCardActive('vouchers') }">
          <div class="card-icon voucher-icon">📄</div>
          <div class="card-content">
            <h3>会计凭证</h3>
            <div class="stat-number">{{ (statistics.voucherStats.totalVoucherCount || statistics.voucherStats.totalCount || 0) }}</div>
            <div class="stat-details">
              <span>AI生成: {{ (statistics.voucherStats.aiGeneratedVoucherCount || statistics.voucherStats.aiGeneratedCount || 0) }}</span>
              <span>待生成: {{ ((statistics.voucherStats.pendingVoucherDocumentCount || 0) + (statistics.voucherStats.pendingVoucherReceiptCount || 0)) }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选凭证</div>
        </div>

        <div class="stat-card" @click="filterByType('ai-processing')" :class="{ 'active': isCardActive('ai-processing') }">
          <div class="card-icon ai-icon">🤖</div>
          <div class="card-content">
            <h3>AI处理状态</h3>
            <div class="stat-number">{{ statistics.aiProcessStats.totalProcessCount || 0 }}</div>
            <div class="stat-details">
              <span>已完成: {{ statistics.aiProcessStats.completedCount || 0 }}</span>
              <span>运行中: {{ statistics.aiProcessStats.runningCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选AI处理</div>
        </div>
      </div>
    </div>

    <!-- 智能搜索筛选区域 - 默认收起 -->
    <div class="search-section" :class="{ 'collapsed': searchPanelCollapsed }">
      <div class="search-header" @click="toggleSearchPanel">
        <h3>
          <i class="icon-search"></i>
          智能搜索筛选
        </h3>
        <button class="btn btn-toggle">
          <i :class="searchPanelCollapsed ? 'icon-down' : 'icon-up'"></i>
          {{ searchPanelCollapsed ? '展开' : '收起' }}
        </button>
      </div>

      <div class="search-content" v-show="!searchPanelCollapsed">
        <div class="search-form-container">
          <!-- 第一行：搜索框和操作按钮 -->
          <div class="search-row">
            <div class="search-input-group">
              <input type="text" 
                     v-model="searchForm.keyword" 
                     placeholder="搜索编号、摘要、交易方..." 
                     @keyup.enter="executeSearch"
                     class="search-input">
              <i class="icon-search search-icon"></i>
            </div>
            <div class="search-actions">
              <button class="btn btn-primary" @click="executeSearch" :disabled="searchLoading">
                <i class="icon-search"></i>
                搜索
              </button>
              <button class="btn btn-default" @click="resetAndSearch">
                <i class="icon-refresh"></i>
                重置
              </button>
              <button class="btn btn-default" @click="saveSearchScheme">
                <i class="icon-bookmark"></i>
                保存方案
              </button>
            </div>
          </div>

          <!-- 第二行：数据类型筛选 -->
          <div class="filter-row">
            <div class="filter-group">
              <label>数据类型</label>
              <div class="checkbox-group">
                <label class="checkbox-item">
                  <input type="checkbox" value="DOCUMENT" v-model="searchForm.entityTypes">
                  <span class="checkbox-label">票据</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" value="RECEIPT" v-model="searchForm.entityTypes">
                <span class="checkbox-label">银行回单</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" value="DOCUMENT_GROUP" v-model="searchForm.entityTypes">
                  <span class="checkbox-label">票据组</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" value="RECEIPT_GROUP" v-model="searchForm.entityTypes">
                <span class="checkbox-label">银行回单组</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" value="VOUCHER" v-model="searchForm.entityTypes">
                  <span class="checkbox-label">凭证</span>
                </label>
              </div>
            </div>
          </div>

          <!-- 第三行：状态和条件筛选 -->
          <div class="filter-row">
            <div class="filter-group">
              <label>归并状态</label>
              <select v-model="searchForm.mergeStatus" class="select-input">
                <option value="ALL">全部</option>
                <option value="MERGED">已归并</option>
                <option value="UNMERGED">未归并</option>
              </select>
            </div>
            <div class="filter-group">
              <label>关联状态</label>
              <select v-model="searchForm.relationStatus" class="select-input">
                <option value="ALL">全部</option>
                <option value="RELATED">已关联</option>
                <option value="UNRELATED">未关联</option>
              </select>
            </div>
            <div class="filter-group">
              <label>AI处理状态</label>
              <select v-model="searchForm.aiProcessStatus" class="select-input">
                <option value="ALL">全部</option>
                <option value="PROCESSED">已处理</option>
                <option value="PENDING">待处理</option>
              </select>
            </div>
            <div class="filter-group">
              <label>日期范围</label>
              <input type="date" v-model="searchForm.startDate" class="date-input">
              <span class="date-separator">至</span>
              <input type="date" v-model="searchForm.endDate" class="date-input">
            </div>
            <div class="filter-group">
              <label>金额范围</label>
              <div class="amount-range">
                <input type="number" v-model="searchForm.minAmount" placeholder="最小" class="amount-input">
                <span>-</span>
                <input type="number" v-model="searchForm.maxAmount" placeholder="最大" class="amount-input">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 主数据区域 -->
    <div class="main-data-section">
      <!-- 工具栏 -->
      <div class="toolbar">
        <div class="view-controls">
          <div class="view-switcher">
            <button class="btn" :class="{ 'btn-primary': currentView === 'list', 'btn-default': currentView !== 'list' }" 
                    @click="switchView('list')">
              <i class="icon-list"></i>
              列表视图
            </button>
            <button class="btn" :class="{ 'btn-primary': currentView === 'graph', 'btn-default': currentView !== 'graph' }" 
                    @click="switchView('graph')">
              <i class="icon-network"></i>
              关系图谱
            </button>
          </div>
          
          <div class="toolbar-actions">
            <button class="btn btn-default" @click="refreshData" :disabled="refreshLoading">
              <i class="icon-refresh"></i>
              刷新
            </button>
            <button class="btn btn-default" @click="exportData">
              <i class="icon-download"></i>
              导出
            </button>
          </div>
        </div>
      </div>

      <!-- 数据表格 -->
      <div class="table-container" v-if="currentView === 'list'">
        <div class="table-wrapper">
          <table class="data-table">
            <thead>
              <tr>
                <th class="checkbox-col">
                  <input type="checkbox" 
                         @change="selectAll" 
                         :checked="isAllSelected"
                         :indeterminate="isIndeterminate">
                </th>
                <th class="type-col">类型</th>
                <th class="no-col">编号</th>
                <th class="summary-col">摘要/描述</th>
                <th class="amount-col">金额</th>
                <th class="date-col">日期</th>
                <th class="status-col">状态</th>
                <th class="action-col">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="tableLoading" class="loading-row">
                <td colspan="8" class="loading-cell">
                  <div class="loading-content">
                    <i class="icon-loading spinning"></i>
                    数据加载中...
                  </div>
                </td>
              </tr>
              <tr v-else-if="!tableData || tableData.length === 0" class="empty-row">
                <td colspan="8" class="empty-cell">
                  <div class="empty-content">
                    <i class="icon-inbox"></i>
                    暂无数据
                  </div>
                </td>
              </tr>
              <tr v-else 
                  v-for="(item, index) in tableData" 
                  :key="item.id || index"
                  @click="onRowClick(item, index)"
                  :class="{ 'selected': selectedItems.includes(item.id) }"
                  class="data-row">
                <td class="checkbox-col">
                  <input type="checkbox" 
                         :checked="selectedItems.includes(item.id)"
                         @change="toggleSelection(item)"
                         @click.stop>
                </td>
                <td class="type-col">
                  <span class="entity-type-badge" :class="getEntityTypeClass(item.entity_type)">
                    {{ getEntityTypeLabel(item.entity_type) }}
                  </span>
                </td>
                <td class="no-col">
                  <span class="entity-no">{{ item.entity_no || '-' }}</span>
                </td>
                <td class="summary-col">
                  <div class="summary-content" :title="item.summary">
                    {{ item.summary || '-' }}
                  </div>
                </td>
                <td class="amount-col">
                  <span class="amount" v-if="item.amount">
                    ¥{{ formatAmount(item.amount) }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td class="date-col">
                  <span class="date">{{ formatDate(item.entity_date) }}</span>
                </td>
                <td class="status-col">
                  <div class="status-badges">
                    <span class="status-badge merge" :class="getMergeStatusClass(item.merge_status)">
                      {{ getMergeStatusLabel(item.merge_status) }}
                    </span>
                    <span class="status-badge relation" :class="getRelationStatusClass(item.relation_status)">
                      {{ getRelationStatusLabel(item.relation_status) }}
                    </span>
                  </div>
                </td>
                <td class="action-col">
                  <div class="action-buttons">
                    <button class="action-btn view" @click.stop="viewDetails(item)" title="查看详情">
                      <i class="icon-eye"></i>
                    </button>
                    <button class="action-btn edit" @click.stop="editItem(item)" title="编辑">
                      <i class="icon-edit"></i>
                    </button>
                    <button class="action-btn delete" @click.stop="deleteItem(item)" title="删除">
                      <i>🗑️</i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- 分页 -->
        <div class="pagination-container">
          <div class="pagination-info">
            共 {{ pagination.total }} 条记录，第 {{ pagination.current }} / {{ Math.ceil(pagination.total / pagination.pageSize) }} 页
          </div>
          <div class="pagination-controls">
            <button class="page-btn" 
                    @click="goToPage(1)" 
                    :disabled="pagination.current === 1">
              首页
            </button>
            <button class="page-btn" 
                    @click="goToPage(pagination.current - 1)" 
                    :disabled="pagination.current === 1">
              上一页
            </button>
            <span class="page-numbers">
              <button v-for="page in getPageNumbers()" 
                      :key="page"
                      class="page-number"
                      :class="{ 'active': page === pagination.current }"
                      @click="goToPage(page)">
                {{ page }}
              </button>
            </span>
            <button class="page-btn" 
                    @click="goToPage(pagination.current + 1)" 
                    :disabled="pagination.current >= Math.ceil(pagination.total / pagination.pageSize)">
              下一页
            </button>
            <button class="page-btn" 
                    @click="goToPage(Math.ceil(pagination.total / pagination.pageSize))" 
                    :disabled="pagination.current >= Math.ceil(pagination.total / pagination.pageSize)">
              末页
            </button>
            <select class="page-size-select" v-model="pagination.pageSize" @change="onPageSizeChange">
              <option value="10">10条/页</option>
              <option value="20">20条/页</option>
              <option value="50">50条/页</option>
              <option value="100">100条/页</option>
            </select>
          </div>
        </div>
      </div>

      <!-- 关系图谱视图 -->
      <div class="graph-container" v-if="currentView === 'graph'">
        <div class="graph-placeholder">
          <i class="icon-network"></i>
          <p>关系图谱功能开发中...</p>
        </div>
      </div>
    </div>

    <!-- 底部快速操作工具栏 -->
    <div class="quick-toolbar" v-if="selectedItems.length > 0">
      <div class="selected-info">
        已选择 {{ selectedItems.length }} 项
      </div>
      <div class="quick-actions">
        <button class="btn btn-primary" @click="batchAiRelation" :disabled="selectedItems.length === 0">
          <i class="icon-link"></i>
          批量AI关联
        </button>
        <button class="btn btn-primary" @click="batchAiMerge" :disabled="selectedItems.length === 0">
          <i class="icon-merge"></i>
          批量AI归并
        </button>
        <button class="btn btn-primary" @click="batchGenerateVoucher" :disabled="selectedItems.length === 0">
          <i class="icon-voucher"></i>
          批量生成凭证
        </button>
        <button class="btn btn-default" @click="aiSettings">
          <i class="icon-settings"></i>
          AI设置
        </button>
      </div>
    </div>

    <!-- 详情信息浮窗 -->
    <div class="detail-modal" v-if="showDetailModal" @click="closeDetailModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="icon-detail"></i>
            {{ selectedItem ? getEntityTypeLabel(selectedItem.entity_type) : '' }}详情信息
          </h3>
          <button class="close-btn" @click="closeDetailModal">
            <i class="icon-close"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="selectedItem" class="detail-container">

            <!-- 基本信息 -->
            <div class="detail-section">
              <h4>
                <i class="icon-info"></i>
                基本信息
              </h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="label">数据类型:</span>
                  <span class="value">
                    <span class="entity-type-badge" :class="getEntityTypeClass(selectedItem.entity_type)">
                      {{ getEntityTypeLabel(selectedItem.entity_type) }}
                    </span>
                  </span>
                </div>
                <div class="detail-item">
                  <span class="label">编号:</span>
                  <span class="value">{{ selectedItem.entity_no || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">金额:</span>
                  <span class="value amount-value">¥{{ formatAmount(selectedItem.amount) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">日期:</span>
                  <span class="value">{{ formatDate(selectedItem.entity_date) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">摘要:</span>
                  <span class="value">{{ selectedItem.summary || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">交易方:</span>
                  <span class="value">{{ selectedItem.counterparty || selectedItem.issuer || '-' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">创建时间:</span>
                  <span class="value">{{ formatDateTime(selectedItem.created_at) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">更新时间:</span>
                  <span class="value">{{ formatDateTime(selectedItem.updated_at) }}</span>
                </div>
              </div>
            </div>

            <!-- 业务信息 -->
            <div class="detail-section">
              <h4>
                <i class="icon-business"></i>
                业务信息
              </h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="label">业务状态:</span>
                  <span class="value">{{ selectedItem.status || '正常' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">归并组ID:</span>
                  <span class="value">{{ selectedItem.group_id || '未归并' }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">关联数量:</span>
                  <span class="value">{{ selectedItem.relation_count || 0 }}个</span>
                </div>
                <div class="detail-item" v-if="selectedItem.entity_type_name">
                  <span class="label">业务类型:</span>
                  <span class="value">{{ selectedItem.entity_type_name }}</span>
                </div>
              </div>
            </div>

            <!-- 状态信息 -->
            <div class="detail-section">
              <h4>
                <i class="icon-status"></i>
                状态信息
              </h4>
              <div class="status-grid">
                <div class="status-item">
                  <span class="status-label">归并状态:</span>
                  <span class="status-tag merge" :class="getMergeStatusClass(selectedItem.merge_status)">
                    {{ getMergeStatusLabel(selectedItem.merge_status) }}
                  </span>
                </div>
                <div class="status-item">
                  <span class="status-label">关联状态:</span>
                  <span class="status-tag relation" :class="getRelationStatusClass(selectedItem.relation_status)">
                    {{ getRelationStatusLabel(selectedItem.relation_status) }}
                  </span>
                </div>
                <div class="status-item">
                  <span class="status-label">AI处理状态:</span>
                  <span class="status-tag ai" :class="getAiProcessStatusClass(selectedItem.ai_process_status)">
                    {{ getAiProcessStatusLabel(selectedItem.ai_process_status) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 操作历史 -->
            <div class="detail-section">
              <h4>
                <i class="icon-history"></i>
                操作历史
                <button class="btn btn-small btn-default" @click="refreshOperationHistory" :disabled="historyLoading">
                  <i class="icon-refresh" :class="{ 'spinning': historyLoading }"></i>
                  刷新
                </button>
              </h4>

              <div class="history-tabs">
                <button class="history-tab"
                        :class="{ 'active': activeHistoryTab === 'all' }"
                        @click="activeHistoryTab = 'all'">
                  全部历史
                </button>
                <button class="history-tab"
                        :class="{ 'active': activeHistoryTab === 'ai' }"
                        @click="activeHistoryTab = 'ai'">
                  AI处理
                </button>
                <button class="history-tab"
                        :class="{ 'active': activeHistoryTab === 'manual' }"
                        @click="activeHistoryTab = 'manual'">
                  人工操作
                </button>
              </div>

              <div class="history-content">
                <div v-if="historyLoading" class="history-loading">
                  <i class="icon-loading spinning"></i>
                  加载历史记录中...
                </div>
                <div v-else-if="filteredOperationHistory.length === 0" class="history-empty">
                  <i class="icon-empty"></i>
                  暂无{{ getHistoryTabLabel() }}记录
                </div>
                <div v-else class="history-list">
                  <div v-for="(record, index) in filteredOperationHistory"
                       :key="index"
                       class="history-item">
                    <div class="history-icon">
                      <i :class="getHistoryIcon(record.operation_type)"></i>
                    </div>
                    <div class="history-content-detail">
                      <div class="history-title">
                        <span class="operation-type">{{ record.operation_name }}</span>
                        <span class="operation-time">{{ formatDateTime(record.operation_time) }}</span>
                      </div>
                      <div class="history-description">{{ record.description }}</div>
                      <div class="history-meta">
                        <span class="operator">
                          <i class="icon-user"></i>
                          {{ record.operator || 'AI系统' }}
                        </span>
                        <span class="operation-result" :class="record.result">
                          <i :class="record.result === 'success' ? 'icon-success' : 'icon-error'"></i>
                          {{ record.result === 'success' ? '成功' : '失败' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 关联信息 -->
            <div class="detail-section" v-if="selectedItem.relation_count > 0">
              <h4>
                <i class="icon-link"></i>
                关联信息
                <span class="relation-count">({{ selectedItem.relation_count }}个)</span>
              </h4>
              <div class="relation-list">
                <div v-for="relation in relatedItems" :key="relation.id" class="relation-item">
                  <span class="relation-type">{{ getEntityTypeLabel(relation.entity_type) }}</span>
                  <span class="relation-no">{{ relation.entity_no }}</span>
                  <span class="relation-amount">¥{{ formatAmount(relation.amount) }}</span>
                </div>
              </div>
              <button class="btn btn-small btn-default" @click="loadRelatedItems" v-if="!relatedItems.length">
                <i class="icon-link"></i>
                加载关联数据
              </button>
            </div>

            <!-- 快速操作 -->
            <div class="detail-section">
              <h4>
                <i class="icon-tools"></i>
                快速操作
              </h4>
              <div class="quick-operation-buttons">
                <button class="btn btn-primary" @click="editItem(selectedItem)">
                  <i class="icon-edit"></i>
                  编辑
                </button>
                <button class="btn btn-success" @click="viewFullRecord(selectedItem)">
                  <i class="icon-detail"></i>
                  查看完整记录
                </button>
                <button class="btn btn-warning" @click="duplicateItem(selectedItem)">
                  <i class="icon-copy"></i>
                  复制
                </button>
                <button class="btn btn-danger" @click="deleteItem(selectedItem)">
                  <i>🗑️</i>
                  删除
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UnifiedQueryNative',
  data() {
    return {
      // 统计数据
      statistics: {
        documentStats: { totalCount: 0, mergedCount: 0, relatedCount: 0 },
        receiptStats: { totalCount: 0, mergedCount: 0, relatedCount: 0 },
        mergeGroupStats: { documentGroupCount: 0, receiptGroupCount: 0 },
        relationStats: { totalRelationCount: 0, aiGeneratedRelationCount: 0, manualRelationCount: 0 },
        voucherStats: { totalCount: 0, aiGeneratedCount: 0, pendingCount: 0 },
        aiProcessStats: { totalProcessCount: 0, completedCount: 0, runningCount: 0 }
      },

      // 搜索表单
      searchForm: {
        keyword: '',
        entityTypes: ['DOCUMENT', 'RECEIPT'],
        mergeStatus: 'ALL',
        relationStatus: 'ALL',
        aiProcessStatus: 'ALL',
        startDate: '',
        endDate: '',
        minAmount: null,
        maxAmount: null
      },

      // 面板状态
      searchPanelCollapsed: true, // 默认收起
      showDetailModal: false,

      // 当前视图
      currentView: 'list', // 'list' 或 'graph'

      // 表格数据
      tableData: [],
      tableLoading: false,
      selectedItems: [],
      selectedItem: null,

      // 详情浮窗相关
      activeHistoryTab: 'all', // 'all', 'ai', 'manual'
      operationHistory: [],
      historyLoading: false,
      relatedItems: [],

      // 分页
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0
      },

      // 加载状态
      searchLoading: false,
      refreshLoading: false,

      // 激活的筛选卡片
      activeCard: null
    }
  },

  computed: {
    // 表格选择状态
    isAllSelected() {
      return this.tableData.length > 0 && this.selectedItems.length === this.tableData.length
    },

    isIndeterminate() {
      return this.selectedItems.length > 0 && this.selectedItems.length < this.tableData.length
    },

    // 过滤后的操作历史
    filteredOperationHistory() {
      if (this.activeHistoryTab === 'all') {
        return this.operationHistory
      } else if (this.activeHistoryTab === 'ai') {
        return this.operationHistory.filter(item => item.operation_source === 'AI')
      } else if (this.activeHistoryTab === 'manual') {
        return this.operationHistory.filter(item => item.operation_source === 'MANUAL')
      }
      return []
    }
  },

  mounted() {
    this.loadStatistics()
    this.executeSearch()
  },

  methods: {
    // 加载统计数据
    async loadStatistics() {
      try {
        const response = await Ajax.get('/unified-query/statistics')
        if (response.success) {
          this.statistics = response.data
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
      }
    },

    // 执行搜索
    async executeSearch() {
      this.tableLoading = true
      this.searchLoading = true

      try {
        // 构建查询数据，按照后端期望的格式
        const queryData = {
          condition: {
            keyword: this.searchForm.keyword,
            entityTypes: this.searchForm.entityTypes,
            mergeStatus: this.searchForm.mergeStatus,
            relationStatus: this.searchForm.relationStatus,
            aiProcessStatus: this.searchForm.aiProcessStatus,
            // 返回真实关联明细，去除示例数据依赖
            includeRelations: true,
            onlyWithRelations: false,
            startDate: this.searchForm.startDate,
            endDate: this.searchForm.endDate,
            minAmount: this.searchForm.minAmount,
            maxAmount: this.searchForm.maxAmount
          },
          pageParam: {
            pageNum: this.pagination.current,
            pageSize: this.pagination.pageSize
          }
        }

        const response = await Ajax.post('/unified-query/all-entities', queryData)

        if (response.success) {
          this.tableData = response.data.records || []
          this.pagination.total = response.data.total || 0
          console.log('查询成功:', this.tableData.length, '条记录')
        } else {
          HeyUI.$Message.error(response.msg || '查询失败')
        }
      } catch (error) {
        console.error('查询失败:', error)
        HeyUI.$Message.error('查询失败，请稍后重试')
      } finally {
        this.tableLoading = false
        this.searchLoading = false
      }
    },

    // 重置搜索
    resetAndSearch() {
      this.searchForm = {
        keyword: '',
        entityTypes: ['DOCUMENT', 'RECEIPT'],
        mergeStatus: 'ALL',
        relationStatus: 'ALL',
        aiProcessStatus: 'ALL',
        startDate: '',
        endDate: '',
        minAmount: null,
        maxAmount: null
      }
      this.pagination.current = 1
      this.activeCard = null
      this.executeSearch()
    },

    // 显示全部数据
    showAllData() {
      this.resetAndSearch()
    },

    // 判断是否显示全部数据
    isShowingAll() {
      const allTypes = ['DOCUMENT', 'RECEIPT', 'DOCUMENT_GROUP', 'RECEIPT_GROUP', 'VOUCHER']
      return this.searchForm.entityTypes.length === allTypes.length &&
             allTypes.every(type => this.searchForm.entityTypes.includes(type)) &&
             this.searchForm.mergeStatus === 'ALL' &&
             this.searchForm.relationStatus === 'ALL' &&
             this.searchForm.aiProcessStatus === 'ALL' &&
             !this.searchForm.keyword &&
             !this.searchForm.startDate &&
             !this.searchForm.endDate &&
             !this.searchForm.minAmount &&
             !this.searchForm.maxAmount
    },

    // 保存搜索方案
    saveSearchScheme() {
      HeyUI.$Message.info('搜索方案保存功能开发中...')
    },

    // 切换搜索面板
    toggleSearchPanel() {
      this.searchPanelCollapsed = !this.searchPanelCollapsed
    },

    // 按类型筛选
    filterByType(type) {
      this.activeCard = type

      switch (type) {
        case 'documents':
          this.searchForm.entityTypes = ['DOCUMENT']
          // 切换到具体类型时，清理与“关联关系”卡片残留的过滤
          this.searchForm.relationStatus = 'ALL'
          this.searchForm.aiProcessStatus = 'ALL'
          break
        case 'receipts':
          this.searchForm.entityTypes = ['RECEIPT']
          this.searchForm.relationStatus = 'ALL'
          this.searchForm.aiProcessStatus = 'ALL'
          break
        case 'merge-groups':
          this.searchForm.entityTypes = ['DOCUMENT_GROUP', 'RECEIPT_GROUP']
          // 归并组查询中 relation_status 固定为 'UNRELATED'，必须重置为 ALL 才能显示
          this.searchForm.relationStatus = 'ALL'
          this.searchForm.aiProcessStatus = 'ALL'
          break
        case 'relations':
          // 仅排除凭证，其他类型全选
          this.searchForm.entityTypes = ['DOCUMENT', 'RECEIPT', 'DOCUMENT_GROUP', 'RECEIPT_GROUP']
          this.searchForm.relationStatus = 'RELATED'
          break
        case 'vouchers':
          this.searchForm.entityTypes = ['VOUCHER']
          this.searchForm.relationStatus = 'ALL'
          this.searchForm.aiProcessStatus = 'ALL'
          break
        case 'ai-processing':
          this.searchForm.aiProcessStatus = 'PROCESSED'
          break
      }

      this.pagination.current = 1
      this.executeSearch()
    },

    // 判断卡片是否激活
    isCardActive(type) {
      return this.activeCard === type
    },

    // 切换视图
    switchView(view) {
      this.currentView = view
    },

    // 刷新数据
    refreshData() {
      this.refreshLoading = true
      this.loadStatistics()
      this.executeSearch().finally(() => {
        this.refreshLoading = false
      })
    },

    // 导出数据
    exportData() {
      HeyUI.$Message.info('导出功能开发中...')
    },

    // 表格选择相关方法
    selectAll(event) {
      if (event.target.checked) {
        this.selectedItems = this.tableData.map(item => item.id)
      } else {
        this.selectedItems = []
      }
    },

    toggleSelection(item) {
      const index = this.selectedItems.indexOf(item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(item.id)
      }
    },

    // 行点击
    onRowClick(item, index) {
      this.selectedItem = item
    },

    // 分页相关方法
    goToPage(page) {
      if (page >= 1 && page <= Math.ceil(this.pagination.total / this.pagination.pageSize)) {
        this.pagination.current = page
        this.executeSearch()
      }
    },

    getPageNumbers() {
      const total = Math.ceil(this.pagination.total / this.pagination.pageSize)
      const current = this.pagination.current
      const pages = []

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        }
      }

      return pages
    },

    onPageSizeChange(event) {
      this.pagination.pageSize = parseInt(event.target.value)
      this.pagination.current = 1
      this.executeSearch()
    },

    // 查看详情
    viewDetails(item) {
      this.selectedItem = item
      this.showDetailModal = true
      this.activeHistoryTab = 'all'
      this.operationHistory = []
      this.relatedItems = []
      this.loadOperationHistory()
    },

    // 关闭详情浮窗
    closeDetailModal() {
      this.showDetailModal = false
      this.selectedItem = null
      this.operationHistory = []
      this.relatedItems = []
    },

    // 加载操作历史（真实接口）
    async loadOperationHistory() {
      if (!this.selectedItem) return

      this.historyLoading = true
      try {
        const res = await Ajax.get(`/unified-query/ai-history/${this.selectedItem.entity_type}/${this.selectedItem.entity_id}`)
        if (res && res.success) {
          this.operationHistory = Array.isArray(res.data) ? res.data : []
        } else {
          this.operationHistory = []
        }
      } catch (error) {
        console.error('加载操作历史失败:', error)
        this.operationHistory = []
      } finally {
        this.historyLoading = false
      }
    },

    // 刷新操作历史
    refreshOperationHistory() {
      this.loadOperationHistory()
    },

    // 加载关联数据（真实接口：跨类型关联，前端过滤当前实体）
    async loadRelatedItems() {
      if (!this.selectedItem || this.selectedItem.relation_count === 0) return

      try {
        const queryData = {
          condition: {
            // 可根据需要补充关键字/日期等筛选
          },
          pageParam: { pageNum: 1, pageSize: 10000 }
        }
        const resp = await Ajax.post('/unified-query/relations', queryData)
        if (resp && resp.success) {
          const all = resp.data && Array.isArray(resp.data.records) ? resp.data.records : []
          const relatedToCurrent = all.filter(r =>
            (r.source_type === this.selectedItem.entity_type && String(r.source_id) === String(this.selectedItem.entity_id)) ||
            (r.target_type === this.selectedItem.entity_type && String(r.target_id) === String(this.selectedItem.entity_id))
          )
          this.relatedItems = relatedToCurrent.map(r => {
            const isSourceCurrent = r.source_type === this.selectedItem.entity_type && String(r.source_id) === String(this.selectedItem.entity_id)
            const otherType = isSourceCurrent ? r.target_type : r.source_type
            const otherNo = isSourceCurrent ? (r.target_name || r.target_id) : (r.source_name || r.source_id)
            return {
              id: r.relation_id,
              entity_type: otherType,
              entity_no: otherNo,
              amount: r.relation_amount != null ? r.relation_amount : undefined
            }
          })
        } else {
          this.relatedItems = []
        }
      } catch (error) {
        console.error('加载关联数据失败:', error)
        this.relatedItems = []
      }
    },

    // 编辑项目
    editItem(item) {
      HeyUI.$Message.info('编辑功能开发中...')
    },

    // 删除项目
    deleteItem(item) {
      if (confirm('确定要删除这条记录吗？')) {
        HeyUI.$Message.info('删除功能开发中...')
      }
    },

    // 查看完整记录
    viewFullRecord(item) {
      HeyUI.$Message.info('查看完整记录功能开发中...')
    },

    // 复制项目
    duplicateItem(item) {
      HeyUI.$Message.info('复制功能开发中...')
    },

    // 获取历史标签页标签
    getHistoryTabLabel() {
      const labels = {
        'all': '操作历史',
        'ai': 'AI处理',
        'manual': '人工操作'
      }
      return labels[this.activeHistoryTab] || '操作历史'
    },

    // 获取历史图标
    getHistoryIcon(operationType) {
      const iconMap = {
        'CREATE': 'icon-plus',
        'UPDATE': 'icon-edit',
        'DELETE': 'icon-delete',
        'AI_ANALYSIS': 'icon-ai',
        'AI_MATCH': 'icon-link',
        'AI_MERGE': 'icon-merge',
        'MANUAL_EDIT': 'icon-edit',
        'MANUAL_REVIEW': 'icon-check'
      }
      return iconMap[operationType] || 'icon-info'
    },

    // 获取AI处理状态样式
    getAiProcessStatusClass(status) {
      const classMap = {
        'PROCESSED': 'processed',
        'PENDING': 'pending',
        'FAILED': 'failed'
      }
      return classMap[status] || 'pending'
    },

    // 获取AI处理状态标签
    getAiProcessStatusLabel(status) {
      const labelMap = {
        'PROCESSED': '已处理',
        'PENDING': '待处理',
        'FAILED': '处理失败'
      }
      return labelMap[status] || '待处理'
    },

    // 批量操作
    batchAiRelation() {
      HeyUI.$Message.info('批量AI关联功能开发中...')
    },

    batchAiMerge() {
      HeyUI.$Message.info('批量AI归并功能开发中...')
    },

    batchGenerateVoucher() {
      HeyUI.$Message.info('批量生成凭证功能开发中...')
    },

    aiSettings() {
      HeyUI.$Message.info('AI设置功能开发中...')
    },

    // 实体类型相关方法
    getEntityTypeLabel(entityType) {
      const labelMap = {
        'DOCUMENT': '票据',
        'RECEIPT': '银行回单',
        'DOCUMENT_GROUP': '票据组',
        'RECEIPT_GROUP': '银行回单组',
        'VOUCHER': '凭证'
      }
      return labelMap[entityType] || '未知'
    },

    getEntityTypeClass(entityType) {
      const classMap = {
        'DOCUMENT': 'document',
        'RECEIPT': 'receipt',
        'DOCUMENT_GROUP': 'document-group',
        'RECEIPT_GROUP': 'receipt-group',
        'VOUCHER': 'voucher'
      }
      return classMap[entityType] || 'default'
    },

    // 状态相关方法
    getMergeStatusClass(status) {
      return status === 'MERGED' ? 'merged' : 'unmerged'
    },

    getMergeStatusLabel(status) {
      return status === 'MERGED' ? '已归并' : '未归并'
    },

    getRelationStatusClass(status) {
      return status === 'RELATED' ? 'related' : 'unrelated'
    },

    getRelationStatusLabel(status) {
      return status === 'RELATED' ? '已关联' : '未关联'
    },

    // 格式化方法
    formatAmount(amount) {
      if (!amount) return '0'
      return Number(amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },

    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.getFullYear() + '/' +
             String(d.getMonth() + 1).padStart(2, '0') + '/' +
             String(d.getDate()).padStart(2, '0')
    },

    formatDateTime(datetime) {
      if (!datetime) return '-'
      const d = new Date(datetime)
      return d.getFullYear() + '/' +
             String(d.getMonth() + 1).padStart(2, '0') + '/' +
             String(d.getDate()).padStart(2, '0') + ' ' +
             String(d.getHours()).padStart(2, '0') + ':' +
             String(d.getMinutes()).padStart(2, '0')
    }
  }
}
</script>

<style scoped>
/* 全局样式 */
.unified-query-native {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 按钮样式 */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: white;
  color: #606266;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  white-space: nowrap;
}

.btn:hover {
  background-color: #f5f7fa;
  border-color: #c0c4cc;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #409eff;
  border-color: #409eff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

.btn-primary.active {
  background-color: #337ecc;
  border-color: #337ecc;
}

.btn-default {
  background-color: white;
  border-color: #dcdfe6;
  color: #606266;
}

.btn-danger {
  background-color: #f56c6c;
  border-color: #f56c6c;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #f78989;
  border-color: #f78989;
}

/* 图标样式 */
.icon-analytics::before { content: "📊"; }
.icon-apps::before { content: "⚡"; }
.icon-search::before { content: "🔍"; }
.icon-down::before { content: "▼"; }
.icon-up::before { content: "▲"; }
.icon-refresh::before { content: "🔄"; }
.icon-bookmark::before { content: "🔖"; }
.icon-list::before { content: "📋"; }
.icon-network::before { content: "🕸️"; }
.icon-download::before { content: "⬇️"; }
.icon-loading::before { content: "⏳"; }
.icon-inbox::before { content: "📥"; }
.icon-eye::before { content: "👁️"; }
.icon-edit::before { content: "✏️"; }

.icon-close::before { content: "✖️"; }
.icon-link::before { content: "🔗"; }
.icon-merge::before { content: "🔀"; }
.icon-voucher::before { content: "📄"; }
.icon-settings::before { content: "⚙️"; }
.icon-history::before { content: "📜"; }
.icon-detail::before { content: "📋"; }
.icon-info::before { content: "ℹ️"; }
.icon-business::before { content: "💼"; }
.icon-status::before { content: "🏷️"; }
.icon-tools::before { content: "🔧"; }
.icon-user::before { content: "👤"; }
.icon-success::before { content: "✅"; }
.icon-error::before { content: "❌"; }
.icon-empty::before { content: "📭"; }
.icon-plus::before { content: "➕"; }
.icon-ai::before { content: "🤖"; }
.icon-check::before { content: "✔️"; }
.icon-copy::before { content: "📋"; }

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 智能仪表板 */
.dashboard-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.section-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 600;
}

.dashboard-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  padding: 20px;
}

.stat-card {
  background: white;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  transform: translateY(-2px);
}

.stat-card.active {
  border-color: #409eff;
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
}

.card-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.card-content h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #333;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #409eff;
  margin-bottom: 8px;
}

.stat-details {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.card-filter-hint {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 11px;
  color: #999;
  opacity: 0;
  transition: opacity 0.3s;
}

.stat-card:hover .card-filter-hint {
  opacity: 1;
}

/* 搜索筛选区域 */
.search-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.search-section.collapsed {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  transition: background 0.3s;
}

.search-header:hover {
  background: linear-gradient(135deg, #f0f4ff 0%, #e0e8ff 100%);
}

.search-header h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.btn-toggle {
  background: transparent;
  border: 1px solid #ddd;
  color: #666;
  font-size: 12px;
  padding: 6px 12px;
}

.search-content {
  padding: 20px;
}

.search-form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-row, .filter-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.search-input-group {
  flex: 1;
  position: relative;
  min-width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.search-input:focus {
  outline: none;
  border-color: #409eff;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #c0c4cc;
}

.search-actions {
  display: flex;
  gap: 8px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
  min-width: 60px;
}

.checkbox-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
}

.checkbox-item input[type="checkbox"] {
  margin: 0;
}

.checkbox-label {
  color: #606266;
}

.select-input {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  min-width: 120px;
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  width: 140px;
}

.date-separator {
  color: #666;
  margin: 0 4px;
}

.amount-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.amount-input {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  width: 80px;
}

/* 主数据区域 */
.main-data-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.toolbar {
  padding: 16px 20px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafbfc;
}

.view-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.view-switcher {
  display: flex;
  gap: 0;
}

.view-switcher .btn {
  border-radius: 0;
  border-right: none;
}

.view-switcher .btn:first-child {
  border-radius: 6px 0 0 6px;
}

.view-switcher .btn:last-child {
  border-radius: 0 6px 6px 0;
  border-right: 1px solid #dcdfe6;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
}

/* 表格样式 */
.table-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-wrapper {
  flex: 1;
  overflow: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background: #f8f9fa;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 10;
}

.data-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

.data-row:hover {
  background-color: #f8f9fa;
}

.data-row.selected {
  background-color: #e3f2fd;
}

/* 列宽设置 */
.checkbox-col { width: 50px; text-align: center; }
.type-col { width: 80px; }
.no-col { width: 150px; }
.summary-col { min-width: 200px; }
.amount-col { width: 120px; text-align: right; }
.date-col { width: 120px; }
.status-col { width: 150px; }
.action-col { width: 120px; text-align: center; }

/* 实体类型徽章 */
.entity-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.entity-type-badge.document { background-color: #007bff; }
.entity-type-badge.receipt { background-color: #28a745; }
.entity-type-badge.document-group { background-color: #6f42c1; }
.entity-type-badge.receipt-group { background-color: #20c997; }
.entity-type-badge.voucher { background-color: #fd7e14; }
.entity-type-badge.default { background-color: #6c757d; }

/* 状态徽章 */
.status-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
}

.status-badge.merge.merged { background-color: #d4edda; color: #155724; }
.status-badge.merge.unmerged { background-color: #f8d7da; color: #721c24; }
.status-badge.relation.related { background-color: #d1ecf1; color: #0c5460; }
.status-badge.relation.unrelated { background-color: #ffeaa7; color: #856404; }

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.action-btn {
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  color: white;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn.view { background-color: #007bff; }
.action-btn.edit { background-color: #28a745; }
.action-btn.delete { background-color: #dc3545; }

.action-btn:hover.view { background-color: #0056b3; }
.action-btn:hover.edit { background-color: #1e7e34; }
.action-btn:hover.delete { background-color: #c82333; }

/* 加载和空状态 */
.loading-cell, .empty-cell {
  text-align: center;
  padding: 40px 20px;
}

.loading-content, .empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #6c757d;
}

.loading-content i, .empty-content i {
  font-size: 24px;
}

/* 摘要内容 */
.summary-content {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 分页样式 */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.pagination-info {
  color: #6c757d;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 36px;
  text-align: center;
  transition: all 0.2s;
}

.page-number:hover {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.page-number.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.page-size-select {
  padding: 6px 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  background: white;
}

/* 关系图谱 */
.graph-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.graph-placeholder {
  text-align: center;
  color: #6c757d;
}

.graph-placeholder i {
  font-size: 48px;
  margin-bottom: 16px;
}

/* 快速工具栏 */
.quick-toolbar {
  position: fixed;
  bottom: 20px;
  left: 260px;
  right: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  max-width: calc(100vw - 300px);
}

.selected-info {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.quick-actions {
  display: flex;
  gap: 12px;
}

/* 详情浮窗 */
.detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 95%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e8e8e8;
  background: #f8f9fa;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-btn:hover {
  background: #e8e8e8;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

/* 详情容器 */
.detail-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.detail-section h4 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.detail-section h4 i {
  font-size: 18px;
}

/* 详情网格布局 */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.detail-item .label {
  min-width: 80px;
  color: #666;
  font-weight: 500;
  font-size: 13px;
}

.detail-item .value {
  flex: 1;
  color: #333;
  font-size: 14px;
  word-break: break-all;
}

.amount-value {
  font-weight: 600;
  color: #007bff;
  font-size: 16px;
}

/* 状态网格 */
.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.status-label {
  min-width: 80px;
  color: #666;
  font-weight: 500;
  font-size: 13px;
}

.status-tag {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-tag.merge.merged { background-color: #d4edda; color: #155724; }
.status-tag.merge.unmerged { background-color: #f8d7da; color: #721c24; }
.status-tag.relation.related { background-color: #d1ecf1; color: #0c5460; }
.status-tag.relation.unrelated { background-color: #ffeaa7; color: #856404; }
.status-tag.ai.processed { background-color: #d4edda; color: #155724; }
.status-tag.ai.pending { background-color: #fff3cd; color: #856404; }
.status-tag.ai.failed { background-color: #f8d7da; color: #721c24; }

/* 操作历史 */
.history-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  background: #e9ecef;
  border-radius: 6px;
  padding: 4px;
}

.history-tab {
  flex: 1;
  padding: 8px 16px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s;
}

.history-tab:hover {
  background: rgba(255, 255, 255, 0.5);
}

.history-tab.active {
  background: white;
  color: #007bff;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.history-content {
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  min-height: 200px;
}

.history-loading, .history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #666;
  gap: 8px;
}

.history-loading i, .history-empty i {
  font-size: 24px;
  margin-bottom: 8px;
}

.history-list {
  padding: 16px;
}

.history-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
}

.history-item:last-child {
  border-bottom: none;
}

.history-item:hover {
  background: #f8f9fa;
}

.history-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.history-icon i {
  font-size: 16px;
  color: #666;
}

.history-content-detail {
  flex: 1;
}

.history-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.operation-type {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.operation-time {
  font-size: 12px;
  color: #999;
}

.history-description {
  color: #666;
  font-size: 13px;
  margin-bottom: 8px;
  line-height: 1.4;
}

.history-meta {
  display: flex;
  gap: 16px;
  align-items: center;
}

.operator, .operation-result {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.operator {
  color: #666;
}

.operation-result.success {
  color: #28a745;
}

.operation-result.failed {
  color: #dc3545;
}

/* 关联信息 */
.relation-count {
  font-size: 14px;
  color: #666;
  font-weight: normal;
}

.relation-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.relation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.relation-type {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: #e9ecef;
  color: #666;
}

.relation-no {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.relation-amount {
  font-weight: 500;
  color: #007bff;
}

/* 快速操作 */
.quick-operation-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn-small {
  padding: 6px 12px;
  font-size: 12px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .dashboard-cards {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }

  .quick-toolbar {
    left: 20px;
    max-width: calc(100vw - 40px);
  }
}

@media (max-width: 768px) {
  .unified-query-native {
    padding: 10px;
  }

  .dashboard-cards {
    grid-template-columns: 1fr;
  }

  .search-row, .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input-group {
    min-width: auto;
  }

  .view-controls {
    flex-direction: column;
    gap: 12px;
  }

  .quick-toolbar {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .modal-content {
    width: 95%;
    margin: 20px;
  }
}
</style>
